import React from "react";
import Title from "../../ui/Title";

const CenteredCare = () => {
  return (
    <div
      id="person-centered-care-caregiver-centered-care"
      className="scroll-mt-24"
    >
      <Title
        title="Person-centered care including caregiver-centered care"
        className="mb-4"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          Currently, there is an increasing emphasis on healthcare to be
          person-centered.
        </p>

        <p>
          According to a{" "}
          <a
            href="https://www.health.org.uk/sites/default/files/PersonCentredCareMadeSimple.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            2016 report
          </a>{" "}
          from The Health Foundation, a UK-based non-profit and research
          organization, ‘person-centered care puts the care recipient's needs
          and preferences at the center of care.’ This involves giving them more
          agency in the treatment process. This approach considers what is
          important to the person, actively involves them in decisions about
          their care and treatment, identifies their goals, and works towards
          achieving them.
        </p>

        <p>
          The Health Foundation has put forward four principles that
          characterize person-centered care:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>Affording people dignity, compassion, and respect</li>
          <li>Offering coordinated care, support, or treatment</li>
          <li>Offering personalized care, support, or treatment</li>
          <li>
            Supporting people in recognizing and developing their own strengths
            and abilities will enable them to live an independent and fulfilling
            life
          </li>
        </ul>

        <p>
          Person-centered care is a great step forward in care and treatment, as
          it is respectful and personalized to the unique needs of the patient.
        </p>

        <p>
          In many cases, families of the patient are active participants in the
          care team when treatment is done in a person-centered manner. While it
          offers great advantages, it's also important to consider the unique
          needs of caregivers.
        </p>

        <p>
          This raises an important question, can care be also
          caregiver-centered?
        </p>

        <p>
          The University of Alberta’s framework on caregiver-centered care
          offers a great{" "}
          <a
            href="https://www.ualberta.ca/en/medicine/resources/education/learning-design-studio/featured-projects/caregiver-centered-care.html"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            perspective
          </a>
          . According to the University, ‘caregiver centered care is
          person-centered care for family caregivers. This involves supporting
          family caregivers in their caregiving roles in making decisions about
          services, care management, and advocacy.’
        </p>

        <p>
          The framework presents caregiver-centered care as “not shifting
          responsibilities to families but building a collaborative relationship
          between caregivers and healthcare and social care professionals
          supporting caregivers in their caregiving role about decisions, care
          management, advocacy, etc.”
        </p>

        <p>
          This{" "}
          <a
            href="https://drive.google.com/file/d/1gdBAd997n4eKpZiGN5QahmEaEcc9QXQ0/view"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Competency Framework
          </a>{" "}
          by the University of Alberta helps healthcare providers evaluate
          themselves and learn more about providing care that is
          caregiver-centered.
        </p>

        <div className="flex flex-col items-center gap-2">
          <img
            src="/assets/img/center-care.png"
            alt="Competency-Based Caregiver-Centered Care Education"
          />
        </div>

        <p className="text-center !mt-0 italic">
          Source:{" "}
          <a
            href="https://cdn.prod.website-files.com/60105a5edd88603ee79d7bda/663902b1eb1e81eed95b372e_2024%20April%20%20Caregiver%20Centered%20CAreCompetency%20Framework%20cover%20updated.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Caregiver-Centered Care
          </a>{" "}
          (University of Alberta)
        </p>

        <p>
          Emerging healthcare reforms and increased emphasis on healthcare being
          person-centered make it important to also consider caregivers as a
          critical part of the healthcare system. While valuing the needs and
          preferences of patients, healthcare reforms must also put emphasis on
          the needs of caregivers, so that their challenges are considered and
          addressed. Initiatives like ‘caregiver centered care’, by the
          University of Alberta mark a positive step towards building the much
          needed perspective of acknowledging and supporting caregivers along
          with the patient and improving treatment outcomes.
        </p>
      </div>
    </div>
  );
};

export default CenteredCare;
